/**
 * Field Definitions Store
 *
 * Zustand store with persist middleware for managing Pipedrive field definitions.
 * Part of Field Management Integration - Phase 1: Backend Foundation & Field Definition API
 *
 * Features:
 * - Client-side caching with 1-hour TTL
 * - Offline support via localStorage persistence
 * - Pagination and filtering
 * - Field lookup by hash or ID
 * - Cache invalidation on Field Manager sync
 *
 * Created: 2025-12-19
 * Phase: Field Management Integration - Phase 1
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient, handleApiError } from '@shared/services/apiClient';
import { logger } from '@shared/utils/logger';

/**
 * Field definition interface matching backend model
 */
export interface FieldDefinition {
  id: number;
  field_key: string;
  field_name: string;
  field_type: string;
  entity_type: 'person' | 'organization' | 'deal';
  description?: string;
  is_custom: boolean;
  is_required: boolean;
  is_editable: boolean;
  is_active: boolean;
  display_order: number;
  section_name?: string;
  max_length?: number;
  min_value?: number;
  max_value?: number;
  source_file?: string;
  source_line?: number;
  created_at?: string;
  updated_at?: string;
}

/**
 * API response interface for GET /api/field-definitions
 */
export interface FieldDefinitionsResponse {
  fields: FieldDefinition[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

/**
 * Query parameters for fetching fields
 */
export interface FetchFieldsParams {
  entity_type?: 'person' | 'organization' | 'deal';
  field_type?: string;
  search?: string;
  page?: number;
  limit?: number;
  active_only?: boolean;
}

/**
 * Field Definitions Store State
 */
interface FieldDefinitionsState {
  // Data
  fields: FieldDefinition[];
  total: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  lastFetchTime: number | null;

  // Entity Type Filtering
  currentEntityType: 'person' | 'organization' | 'deal' | null;

  // UI State
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchFields: (params?: FetchFieldsParams) => Promise<void>;
  fetchNextPage: () => Promise<void>;
  setEntityType: (entityType: 'person' | 'organization' | 'deal') => Promise<void>;
  getFieldByHash: (hash: string) => FieldDefinition | undefined;
  getFieldById: (id: number) => FieldDefinition | undefined;
  refreshFields: (params?: FetchFieldsParams) => Promise<void>;
  clearCache: () => void;
}

/**
 * Cache TTL (1 hour in milliseconds)
 */
const CACHE_TTL_MS = 3600000; // 1 hour

/**
 * Field Definitions Zustand Store
 *
 * Usage:
 * ```typescript
 * const { fields, fetchFields, setEntityType, fetchNextPage } = useFieldDefinitionsStore();
 *
 * // Set entity type (auto-fetches)
 * await setEntityType('person');
 *
 * // Fetch next page (pagination)
 * await fetchNextPage();
 *
 * // Get field by hash
 * const field = getFieldByHash('bc5b330241a761e0411b44ab3b4f24211f69929d');
 *
 * // Force refresh
 * await refreshFields();
 * ```
 */
export const useFieldDefinitionsStore = create<FieldDefinitionsState>()(
  persist(
    (set, get) => ({
      // Initial State
      fields: [],
      total: 0,
      currentPage: 1,
      pageSize: 50,
      totalPages: 0,
      lastFetchTime: null,
      currentEntityType: null,
      isLoading: false,
      error: null,

      /**
       * Fetch field definitions from API with caching
       *
       * Checks cache TTL before making API call. Uses cached data if valid.
       * Now supports entity-type filtering and pagination.
       *
       * @param params - Query parameters for filtering and pagination
       */
      fetchFields: async (params: FetchFieldsParams = {}) => {
        const state = get();

        // Use currentEntityType if params.entity_type not provided
        const entityType = params.entity_type || state.currentEntityType;

        // Check cache TTL only if entity type matches and no explicit page change
        if (
          state.lastFetchTime &&
          entityType === state.currentEntityType &&
          (!params.page || params.page === state.currentPage)
        ) {
          const cacheAge = Date.now() - state.lastFetchTime;
          if (cacheAge < CACHE_TTL_MS) {
            logger.log(
              `[FieldDefinitionsStore] Cache hit (age: ${Math.floor(cacheAge / 1000)}s, TTL: ${CACHE_TTL_MS / 1000}s)`
            );
            return; // Use cached data
          }
        }

        set({ isLoading: true, error: null });

        try {
          const page = params.page || state.currentPage;
          const limit = params.limit || state.pageSize;

          logger.log('[FieldDefinitionsStore] Fetching from API...', {
            ...params,
            entity_type: entityType,
            page,
            limit,
          });

          const response = await apiClient.get<FieldDefinitionsResponse>('field-definitions', {
            params: {
              entity_type: entityType,
              field_type: params.field_type,
              search: params.search,
              page,
              limit,
              active_only: params.active_only !== undefined ? params.active_only : true,
            },
          });

          const data = response.data;

          set({
            fields: data.fields,
            total: data.total,
            currentPage: data.page,
            pageSize: data.limit,
            totalPages: data.total_pages,
            currentEntityType: entityType,
            lastFetchTime: Date.now(),
            isLoading: false,
            error: null,
          });

          logger.log(
            `[FieldDefinitionsStore] Fetched ${data.fields.length} fields (total: ${data.total}, entity_type: ${entityType})`
          );
        } catch (error) {
          const errorMessage = handleApiError(error);
          logger.error('[FieldDefinitionsStore] Fetch failed:', errorMessage);

          set({
            error: errorMessage,
            isLoading: false,
            // Update lastFetchTime even on error to prevent rapid retries
            lastFetchTime: Date.now(),
          });
        }
      },

      /**
       * Fetch next page of field definitions (for pagination)
       *
       * Appends new fields to existing fields array for infinite scroll.
       */
      fetchNextPage: async () => {
        const state = get();

        // Don't fetch if already loading or no more pages
        if (state.isLoading) {
          logger.log('[FieldDefinitionsStore] Already loading, skipping fetchNextPage');
          return;
        }

        if (state.currentPage >= state.totalPages) {
          logger.log('[FieldDefinitionsStore] No more pages to fetch');
          return;
        }

        const nextPage = state.currentPage + 1;

        set({ isLoading: true, error: null });

        try {
          logger.log(`[FieldDefinitionsStore] Fetching page ${nextPage}...`);

          const response = await apiClient.get<FieldDefinitionsResponse>('field-definitions', {
            params: {
              entity_type: state.currentEntityType,
              page: nextPage,
              limit: state.pageSize,
              active_only: true,
            },
          });

          const data = response.data;

          set((state) => ({
            fields: [...state.fields, ...data.fields], // Merge with existing fields
            total: data.total,
            currentPage: data.page,
            totalPages: data.total_pages,
            lastFetchTime: Date.now(),
            isLoading: false,
            error: null,
          }));

          logger.log(
            `[FieldDefinitionsStore] Fetched page ${nextPage}: ${data.fields.length} fields (total in store: ${get().fields.length})`
          );
        } catch (error) {
          const errorMessage = handleApiError(error);
          logger.error('[FieldDefinitionsStore] fetchNextPage failed:', errorMessage);

          set({
            error: errorMessage,
            isLoading: false,
            lastFetchTime: Date.now(),
          });
        }
      },

      /**
       * Set entity type and fetch fields for that type
       *
       * Clears existing fields and fetches fresh data for the new entity type.
       *
       * @param entityType - Entity type to filter by ('person', 'organization', 'deal')
       */
      setEntityType: async (entityType: 'person' | 'organization' | 'deal') => {
        const state = get();

        // If same entity type, don't refetch
        if (entityType === state.currentEntityType && state.fields.length > 0) {
          logger.log(
            `[FieldDefinitionsStore] Entity type already set to ${entityType}, using cached data`
          );
          return;
        }

        logger.log(`[FieldDefinitionsStore] Switching entity type to: ${entityType}`);

        // Clear fields and reset pagination when switching entity type
        set({
          fields: [],
          currentPage: 1,
          totalPages: 0,
          total: 0,
          currentEntityType: entityType,
          lastFetchTime: null, // Force refetch
        });

        // Fetch fields for new entity type
        await get().fetchFields({ entity_type: entityType });
      },

      /**
       * Get field definition by hash (field_key)
       *
       * @param hash - SHA-1 hash field key (40 characters)
       * @returns Field definition or undefined if not found
       */
      getFieldByHash: (hash: string) => {
        const state = get();
        const field = state.fields.find((f) => f.field_key === hash);

        if (field) {
          logger.debug(`[FieldDefinitionsStore] Field found by hash: ${hash}`);
        } else {
          logger.debug(`[FieldDefinitionsStore] Field not found by hash: ${hash}`);
        }

        return field;
      },

      /**
       * Get field definition by ID
       *
       * @param id - Field definition ID
       * @returns Field definition or undefined if not found
       */
      getFieldById: (id: number) => {
        const state = get();
        const field = state.fields.find((f) => f.id === id);

        if (field) {
          logger.debug(`[FieldDefinitionsStore] Field found by ID: ${id}`);
        } else {
          logger.debug(`[FieldDefinitionsStore] Field not found by ID: ${id}`);
        }

        return field;
      },

      /**
       * Force refresh fields from API (bypass cache)
       *
       * Clears cache timestamp and refetches data.
       *
       * @param params - Query parameters for filtering and pagination
       */
      refreshFields: async (params: FetchFieldsParams = {}) => {
        logger.log('[FieldDefinitionsStore] Force refresh requested');

        // Clear cache timestamp to force refetch
        set({ lastFetchTime: null });

        // Fetch fresh data
        await get().fetchFields(params);
      },

      /**
       * Clear cache and reset store state
       *
       * Called after Field Manager sync to ensure fresh data.
       */
      clearCache: () => {
        logger.log('[FieldDefinitionsStore] Cache cleared');

        set({
          fields: [],
          total: 0,
          currentPage: 1,
          pageSize: 50,
          totalPages: 0,
          currentEntityType: null,
          lastFetchTime: null,
          error: null,
        });
      },
    }),
    {
      name: 'field-definitions-storage', // localStorage key
      partialize: (state) => ({
        // Only persist data, not UI state
        fields: state.fields,
        total: state.total,
        currentPage: state.currentPage,
        pageSize: state.pageSize,
        totalPages: state.totalPages,
        currentEntityType: state.currentEntityType,
        lastFetchTime: state.lastFetchTime,
      }),
    }
  )
);

/**
 * Helper function to check if cache is valid
 *
 * @returns True if cache is within TTL, false otherwise
 */
export const isCacheValid = (): boolean => {
  const state = useFieldDefinitionsStore.getState();

  if (!state.lastFetchTime) return false;

  const cacheAge = Date.now() - state.lastFetchTime;
  return cacheAge < CACHE_TTL_MS;
};

/**
 * Helper function to get cache info (for debugging)
 *
 * @returns Cache information
 */
export const getCacheInfo = () => {
  const state = useFieldDefinitionsStore.getState();

  return {
    is_valid: isCacheValid(),
    field_count: state.fields.length,
    age_seconds: state.lastFetchTime
      ? Math.floor((Date.now() - state.lastFetchTime) / 1000)
      : null,
    ttl_seconds: CACHE_TTL_MS / 1000,
    last_fetch_time: state.lastFetchTime
      ? new Date(state.lastFetchTime).toISOString()
      : null,
  };
};
